import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboardCheck, faPlus } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { HR_NAV } from './nav';

const STATUS_COLORS: Record<string, string> = {
    scheduled: 'secondary', in_progress: 'info', completed: 'success', cancelled: 'dark',
};
const RATING_COLORS: Record<string, string> = {
    exceeds: 'success', meets: 'primary', developing: 'warning', below: 'danger',
};
const STATUSES = ['scheduled', 'in_progress', 'completed', 'cancelled'];
const RATINGS = ['', 'exceeds', 'meets', 'developing', 'below'];

const Reviews: React.FC = () => {
    const { api } = useInternalApi();
    const [reviews, setReviews] = useState<any[]>([]);
    const [summary, setSummary] = useState<any>(null);
    const [byRating, setByRating] = useState<any[]>([]);
    const [people, setPeople] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState({ internal_user_id: '', period_start: '', period_end: '' });
    const [viewing, setViewing] = useState<any>(null);

    useEffect(() => { document.title = 'Performance - Uzima Internal'; }, []);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get('hr-api/reviews/');
            if (r.data?.status === 'success') {
                setReviews(r.data.data.reviews || []);
                setSummary(r.data.data.summary);
                setByRating(r.data.data.by_rating || []);
            }
        } finally { setLoading(false); }
    };

    useEffect(() => {
        load();
        api.get('hr-api/reference/').then(r => { if (r.data?.status === 'success') setPeople(r.data.data.people || []); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = async () => {
        if (!form.internal_user_id || !form.period_start || !form.period_end) {
            Swal.fire({ icon: 'warning', title: 'Missing fields', text: 'Person and review period are required.' });
            return;
        }
        try {
            await api.post('hr-api/reviews/', form);
            setShowAdd(false);
            setForm({ internal_user_id: '', period_start: '', period_end: '' });
            load();
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Could not schedule review.' });
        }
    };

    const patch = async (r: any, payload: any) => {
        try {
            const res = await api.patch(`hr-api/reviews/${r.id}/`, payload);
            if (res.data?.status === 'success' && viewing?.id === r.id) setViewing(res.data.data);
            load();
        } catch { Swal.fire({ icon: 'error', title: 'Error', text: 'Could not update review.' }); }
    };

    return (
        <InternalShell activeDashboard="human_resources" subNav={HR_NAV}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faClipboardCheck} className="me-2 text-primary" />Performance & Reviews</h2>
                    <p className="text-muted mb-0">Review cycles for internal staff</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowAdd(true)}><FontAwesomeIcon icon={faPlus} className="me-2" />Schedule Review</button>
            </div>

            {loading || !summary ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : (
                <>
                    <div className="d-flex gap-2 mb-4 flex-wrap">
                        <span className="badge bg-secondary p-2">Scheduled: {summary.scheduled}</span>
                        <span className="badge bg-info p-2">In progress: {summary.in_progress}</span>
                        <span className="badge bg-success p-2">Completed: {summary.completed}</span>
                        {byRating.map(r => (
                            <span key={r.rating} className={`badge bg-${RATING_COLORS[r.rating] || 'light'} p-2`}>{r.rating}: {r.count}</span>
                        ))}
                    </div>

                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-0">
                            {reviews.length === 0 ? (
                                <div className="text-center py-5 text-muted">No reviews scheduled.</div>
                            ) : (
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="table-light"><tr><th>Person</th><th>Period</th><th>Reviewer</th><th>Status</th><th>Rating</th></tr></thead>
                                    <tbody>
                                        {reviews.map(r => (
                                            <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setViewing(r)}>
                                                <td><strong>{r.person}</strong></td>
                                                <td className="small">{r.period_start} → {r.period_end}</td>
                                                <td className="small">{r.reviewer || '—'}</td>
                                                <td><span className={`badge bg-${STATUS_COLORS[r.status]}`}>{r.status.replace('_', ' ')}</span></td>
                                                <td>{r.rating ? <span className={`badge bg-${RATING_COLORS[r.rating]}`}>{r.rating}</span> : '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </>
            )}

            {showAdd && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Schedule Review</h5>
                                <button type="button" className="btn-close" onClick={() => setShowAdd(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Person</label>
                                    <select className="form-select" value={form.internal_user_id} onChange={(e) => setForm(f => ({ ...f, internal_user_id: e.target.value }))}>
                                        <option value="">Select…</option>
                                        {people.map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                                    </select>
                                </div>
                                <div className="row g-3">
                                    <div className="col-6">
                                        <label className="form-label">Period from</label>
                                        <input type="date" className="form-control" value={form.period_start} onChange={(e) => setForm(f => ({ ...f, period_start: e.target.value }))} />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">Period to</label>
                                        <input type="date" className="form-control" value={form.period_end} onChange={(e) => setForm(f => ({ ...f, period_end: e.target.value }))} />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowAdd(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={submit}>Schedule</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {viewing && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{viewing.person} — {viewing.period_start} to {viewing.period_end}</h5>
                                <button type="button" className="btn-close" onClick={() => setViewing(null)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label small">Status</label>
                                        <select className="form-select form-select-sm" value={viewing.status} onChange={(e) => patch(viewing, { status: e.target.value })}>
                                            {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label small">Rating</label>
                                        <select className="form-select form-select-sm" value={viewing.rating || ''} onChange={(e) => patch(viewing, { rating: e.target.value })}>
                                            {RATINGS.map(r => <option key={r} value={r}>{r || 'Not rated'}</option>)}
                                        </select>
                                    </div>
                                </div>
                                {['strengths', 'development_areas', 'goals'].map(f => (
                                    <div className="mb-3" key={f}>
                                        <label className="form-label small text-capitalize">{f.replace(/_/g, ' ')}</label>
                                        <textarea className="form-control" rows={2} defaultValue={viewing[f]}
                                            onBlur={(e) => e.target.value !== viewing[f] && patch(viewing, { [f]: e.target.value })} />
                                    </div>
                                ))}
                                {viewing.completed_at && <div className="form-text">Completed {new Date(viewing.completed_at).toLocaleString()}</div>}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setViewing(null)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </InternalShell>
    );
};

export default Reviews;
